import React, { useState, useRef, useCallback } from 'react';
import Ajv from 'ajv';
import { Box, Paper, Typography, Button, Alert, useTheme } from '@mui/material';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import MonacoEditor from '../../components/MonacoEditor';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const ajv = new Ajv();

function JsonSchemaValidator() {
  const theme = useTheme();
  const [schema, setSchema] = useState(`{
  "type": "object",
  "properties": {
    "name": {
      "type": "string"
    },
    "email": {
      "type": "string",
      "format": "email"
    }
  },
  "required": ["name", "email"]
}`);
  const [json, setJson] = useState(`{
  "name": "John Doe",
  "email": "john.doe@example.com"
}`);
  const [validationResult, setValidationResult] = useState(null);

  const schemaEditorRef = useRef(null);
  const jsonEditorRef = useRef(null);

  const handleResize = useCallback(() => {
    setTimeout(() => {
      schemaEditorRef.current?.layout();
      jsonEditorRef.current?.layout();
    }, 0);
  }, []);

  const handleValidate = () => {
    try {
      const schemaObj = JSON.parse(schema);
      const jsonObj = JSON.parse(json);
      const validate = ajv.compile(schemaObj);
      const valid = validate(jsonObj);
      setValidationResult({ valid, errors: validate.errors });
      
      // Notify mascot
      if (valid) {
        window.dispatchEvent(new CustomEvent('pet:reaction', { detail: { type: 'success', message: 'JSON Schema Validation Passed!' } }));
      } else {
        window.dispatchEvent(new CustomEvent('pet:reaction', { detail: { type: 'error', message: 'Schema validation failed. Check your data.' } }));
      }
    } catch (e) {
      setValidationResult({ valid: false, errors: [{ message: `JSON Parsing Error: ${e.message}` }] });
      window.dispatchEvent(new CustomEvent('pet:reaction', { detail: { type: 'error', message: 'Yikes! JSON Parsing Error!' } }));
    }
  };

  return (
    <>
      <SEO
        title="JSON Schema Validator | Free Online Tool | UmbrellaCo"
        description="Validate JSON data against JSON Schema instantly. Free online validator tool for developers. Ensures your JSON structures meet strict schema definitions."
        keywords="json schema validator, validate json schema online, ajv validator, json validator online, free json tools"
        url="/json-tools/schema-validator"
        jsonLd={createSoftwareAppSchema(
          'JSON Schema Validator',
          'Validate JSON data against JSON Schema instantly.',
          'https://umbrellaco.in/json-tools/schema-validator',
          ['JSON Schema Validation', 'Syntax Checking', 'AJV Engine']
        )}
      />
      <ToolPageContainer
        title="JSON Schema Validator"
        description="Validate your JSON payload against a JSON Schema to ensure the data structure conforms to your specifications."
        disablePadding
      >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>

      <PanelGroup direction="horizontal" onLayout={handleResize} style={{ flexGrow: 1 }}>
        <Panel>
          <Paper sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center' }}>Schema</Typography>
            <Box sx={{ flexGrow: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
              <MonacoEditor ref={schemaEditorRef} language="json" value={schema} onChange={setSchema} />
            </Box>
          </Paper>
        </Panel>
        <PanelResizeHandle style={{ width: '10px' }} />
        <Panel>
          <Paper sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center' }}>JSON Data</Typography>
            <Box sx={{ flexGrow: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
              <MonacoEditor ref={jsonEditorRef} language="json" value={json} onChange={setJson} />
            </Box>
          </Paper>
        </Panel>
      </PanelGroup>

      <Box sx={{ py: 2 }}>
        <Button variant="contained" onClick={handleValidate} size="large" fullWidth>Validate</Button>
      </Box>

      {validationResult && (
        <Alert
          severity={validationResult.valid ? 'success' : 'error'}
          icon={validationResult.valid ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
        >
          <Typography sx={{ fontWeight: 'bold' }}>
            {validationResult.valid ? 'Validation Successful' : 'Validation Failed'}
          </Typography>
          {!validationResult.valid && (
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginTop: '8px' }}>
              {JSON.stringify(validationResult.errors, null, 2)}
            </pre>
          )}
        </Alert>
      )}
      </Box>
      </ToolPageContainer>
    </>
  );
}

export default JsonSchemaValidator;